import React, { useState } from 'react';
import {
  Clock,
  RefreshCw,
  X,
  AlertCircle,
  CheckCircle2,
  Calendar,
} from 'lucide-react';

interface ExtendSlaDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    additionalHours: number;
    resetToFullCycle: boolean;
    justification: string;
  }) => Promise<void>;
  currentSlaHours: number;
}

const ExtendSlaDialog: React.FC<ExtendSlaDialogProps> = ({
  open,
  onClose,
  onSubmit,
  currentSlaHours,
}) => {
  const [extensionType, setExtensionType] = useState<'add' | 'reset'>('add');
  const [additionalHours, setAdditionalHours] = useState<number>(currentSlaHours || 24);
  const [justification, setJustification] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    // Validation
    if (!justification.trim()) {
      setError('Justification is required');
      return;
    }

    if (additionalHours < 1 || additionalHours > 168) {
      setError('Additional hours must be between 1 and 168 hours (7 days)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onSubmit({
        additionalHours,
        resetToFullCycle: extensionType === 'reset',
        justification: justification.trim(),
      });

      // Reset form and close
      setJustification('');
      setExtensionType('add');
      setAdditionalHours(currentSlaHours || 24);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'فشل تمديد الموعد | Failed to extend deadline');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setJustification('');
      setExtensionType('add');
      setAdditionalHours(currentSlaHours || 24);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
        {/* Dialog Header */}
        <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-orange-700 px-8 py-6 rounded-t-3xl z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">
                تمديد موعد الإصلاح | Extend Deadline
              </h2>
            </div>
            {!loading && (
              <button
                onClick={handleClose}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            )}
          </div>
        </div>

        {/* Dialog Body */}
        <div className="p-8 space-y-6">
          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 flex items-start gap-3 animate-fadeIn">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Extension Type */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Extension Type | نوع التمديد
            </label>
            <div className="space-y-3">
              {/* Add Hours Option */}
              <div
                onClick={() => setExtensionType('add')}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  extensionType === 'add'
                    ? 'bg-brand-50 border-brand-500 shadow-md'
                    : 'bg-white border-gray-200 hover:border-brand-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-1 rounded-full mt-0.5 ${extensionType === 'add' ? 'bg-brand-500' : 'bg-gray-300'}`}>
                    <div className="w-3 h-3 bg-white rounded-full" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className={`w-5 h-5 ${extensionType === 'add' ? 'text-brand-600' : 'text-gray-500'}`} />
                      <p className={`font-bold ${extensionType === 'add' ? 'text-brand-900' : 'text-gray-900'}`}>
                        Add hours to current deadline | إضافة ساعات للموعد الحالي
                      </p>
                    </div>
                    <p className="text-sm text-gray-600">
                      Extends the existing deadline by the specified hours
                    </p>
                  </div>
                </div>
              </div>

              {/* Reset Option */}
              <div
                onClick={() => setExtensionType('reset')}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  extensionType === 'reset'
                    ? 'bg-brand-50 border-brand-500 shadow-md'
                    : 'bg-white border-gray-200 hover:border-brand-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-1 rounded-full mt-0.5 ${extensionType === 'reset' ? 'bg-brand-500' : 'bg-gray-300'}`}>
                    <div className="w-3 h-3 bg-white rounded-full" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <RefreshCw className={`w-5 h-5 ${extensionType === 'reset' ? 'text-brand-600' : 'text-gray-500'}`} />
                      <p className={`font-bold ${extensionType === 'reset' ? 'text-brand-900' : 'text-gray-900'}`}>
                        إعادة تعيين موعد الإصلاح كاملاً من الآن | Reset full deadline from now
                      </p>
                    </div>
                    <p className="text-sm text-gray-600">
                      Starts a fresh {currentSlaHours}-hour deadline cycle from current time
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Hours */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Additional Hours | ساعات إضافية *
            </label>
            <input
              type="number"
              value={additionalHours}
              onChange={(e) => setAdditionalHours(Number(e.target.value))}
              min={1}
              max={168}
              className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl
                       focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none font-medium"
            />
            <p className="mt-2 text-sm text-gray-600">
              Between 1 and 168 hours (7 days)
            </p>
          </div>

          {/* Justification */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Justification (Required) | المبرر (مطلوب) *
            </label>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              rows={4}
              placeholder="أدخل سبب تمديد موعد الإصلاح... / Provide a reason for extending the deadline..."
              className={`w-full px-4 py-3 border-2 rounded-xl transition-all outline-none font-medium resize-none
                ${error === 'Justification is required' ? 'bg-red-50 border-red-500 focus:ring-red-100' : 'bg-gray-50 border-gray-200 focus:border-brand-500 focus:ring-brand-100'}
                focus:ring-4`}
            />
          </div>

          {/* Info Alert */}
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-900 font-bold mb-1">Note</p>
                <p className="text-blue-800 text-sm">
                  The extension will be logged in the system audit trail. The evaluator will be notified about the extension.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Dialog Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-8 py-6 rounded-b-3xl border-t border-gray-200 flex gap-4">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl
                     hover:bg-gray-50 transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel | إلغاء
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !justification.trim()}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-700 text-white rounded-xl
                     hover:from-amber-700 hover:to-orange-800 transition-all shadow-lg font-bold
                     flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Extending...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                تمديد الموعد | Extend
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExtendSlaDialog;
